import { useEffect, useState, useCallback, type RefObject } from 'react';

export function useModalContentHeight<T extends HTMLElement>(
  isOpen: boolean,
  children: React.ReactNode,
  contentRef: RefObject<T>
) {
  const [contentEl, setContentEl] = useState<T | null>(null);
  const [hasScrollableContent, setHasScrollableContent] = useState(false);

  const setContentRef = useCallback((el: T | null) => {
    if (contentRef && 'current' in contentRef) {
      contentRef.current = el as T;
    }
    setContentEl(el);
  }, [contentRef]);

  useEffect(() => {
    const el = contentEl;
    if (!el || !isOpen) return;

    const checkOverflow = () => {
      setHasScrollableContent(el.scrollHeight > el.clientHeight);
    };

    checkOverflow();

    const resizeObserver = new ResizeObserver(checkOverflow);
    resizeObserver.observe(el);

    const mutationObserver = new MutationObserver(checkOverflow);
    mutationObserver.observe(el, { childList: true, subtree: true });

    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [isOpen, contentEl, children]);

  return {
    contentEl,
    hasScrollableContent,
    setContentRef,
  };
}